'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';

interface DisputeCase {
  caseId: string;
  score: string;
  reason: string;
  date: string;
  status: 'open' | 'under_review' | 'resolved' | 'rejected';
  estimatedDays: number;
}

const existingCases: DisputeCase[] = [
  { caseId: 'ISX-DISP-2026-0042', score: 'Risk Score', reason: 'Faux signal de fraude', date: '10 Aoû 2026', status: 'under_review', estimatedDays: 3 },
  { caseId: 'ISX-DISP-2026-0031', score: 'Brand Score', reason: 'Données incorrectes', date: '28 Juil 2026', status: 'resolved', estimatedDays: 0 },
];

const statusMap = {
  open: { label: 'Ouvert', cls: 'bg-info/10 text-info border-info/20' },
  under_review: { label: 'En révision humaine', cls: 'bg-warning/10 text-warning border-warning/20' },
  resolved: { label: 'Résolu', cls: 'bg-positive/10 text-positive border-positive/20' },
  rejected: { label: 'Rejeté', cls: 'bg-negative/10 text-negative border-negative/20' },
};

const scores = ['ISX Score Global', 'Influencer Score', 'Brand Score', 'Risk Score', 'Audience Quality', 'Market Value', 'Campaign Prediction', 'Growth Forecast', 'Professional Reputation Score'];
const reasons = ['Données incorrectes ou obsolètes', 'Méthodologie inadaptée à mon profil', 'Faux signal de fraude détecté', 'Conflit d\'intérêts dans l\'évaluation', 'Erreur de cohorte de comparaison', 'Données de plateforme non synchronisées', 'Autre raison'];

export default function ScoreDisputePage() {
  const [step, setStep] = useState(1);
  const [selectedScore, setSelectedScore] = useState('');
  const [selectedReason, setSelectedReason] = useState('');
  const [evidence, setEvidence] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [newCaseId, setNewCaseId] = useState('');
  const [gdprConsent, setGdprConsent] = useState(false);
  const [activeTab, setActiveTab] = useState<'new' | 'history' | 'gdpr'>('new');

  const handleSubmit = () => {
    const id = `ISX-DISP-2026-${String(Math.floor(Math.random() * 9000 + 1000)).padStart(4, '0')}`;
    setNewCaseId(id);
    setSubmitted(true);
  };

  const resetForm = () => {
    setStep(1); setSelectedScore(''); setSelectedReason(''); setEvidence(''); setSubmitted(false); setNewCaseId(''); setGdprConsent(false);
  };

  return (
    <AppLayout currentPath="/score-dispute">
      <div className="space-y-8 animate-fade-in">
        {/* Header */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-700 uppercase tracking-widest text-negative bg-negative/10 px-2 py-0.5 rounded-full">Dispute Center</span>
          </div>
          <h1 className="text-2xl font-700 text-foreground">Contestation de Score & RGPD</h1>
          <p className="text-sm text-muted-foreground mt-1">Contestation gratuite · Révision humaine garantie · Droits RGPD</p>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-muted rounded-xl p-1 w-fit">
          {[{ key: 'new', label: 'Nouvelle contestation' }, { key: 'history', label: 'Mes dossiers' }, { key: 'gdpr', label: 'Droits RGPD' }].map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key as typeof activeTab)} className={`px-4 py-2 rounded-lg text-xs font-600 transition-all ${activeTab === t.key ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>{t.label}</button>
          ))}
        </div>

        {/* New Dispute */}
        {activeTab === 'new' && (
          <div className="max-w-2xl">
            {submitted ? (
              <div className="bg-card border border-border rounded-2xl p-8 flex flex-col items-center gap-4 text-center">
                <div className="w-16 h-16 rounded-full bg-positive/10 flex items-center justify-center">
                  <Icon name="CheckCircleIcon" size={32} className="text-positive" />
                </div>
                <div>
                  <h2 className="text-lg font-700 text-foreground">Contestation enregistrée</h2>
                  <p className="text-sm text-muted-foreground mt-1">Votre dossier a été créé avec succès</p>
                </div>
                <div className="bg-muted/40 rounded-xl px-6 py-4 w-full">
                  <p className="text-xs text-muted-foreground mb-1">Numéro de dossier</p>
                  <p className="font-mono text-lg font-700 text-primary">{newCaseId}</p>
                </div>
                <div className="space-y-2 text-sm text-muted-foreground w-full text-left">
                  <div className="flex items-center gap-2"><Icon name="ClockIcon" size={14} className="text-muted-foreground" /><span>Délai estimé : 5–7 jours ouvrés</span></div>
                  <div className="flex items-center gap-2"><Icon name="UserIcon" size={14} className="text-muted-foreground" /><span>Tri automatique + révision humaine si nécessaire</span></div>
                  <div className="flex items-center gap-2"><Icon name="ShieldCheckIcon" size={14} className="text-muted-foreground" /><span>Vérification des conflits d'intérêts du reviewer</span></div>
                  <div className="flex items-center gap-2"><Icon name="BellIcon" size={14} className="text-muted-foreground" /><span>Vous serez notifié par email à chaque mise à jour</span></div>
                </div>
                <button onClick={resetForm} className="px-6 py-2 bg-muted hover:bg-muted/80 border border-border rounded-lg text-sm font-600 text-foreground transition-colors">Nouvelle contestation</button>
              </div>
            ) : (
              <div className="bg-card border border-border rounded-2xl overflow-hidden">
                {/* Progress */}
                <div className="px-6 py-4 border-b border-border bg-muted/30">
                  <div className="flex items-center gap-3">
                    {[1, 2, 3].map(s => (
                      <React.Fragment key={s}>
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-700 transition-all ${step >= s ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>{s}</div>
                        {s < 3 && <div className={`flex-1 h-0.5 rounded-full transition-all ${step > s ? 'bg-primary' : 'bg-border'}`} />}
                      </React.Fragment>
                    ))}
                  </div>
                  <div className="flex justify-between mt-2">
                    <span className="text-[10px] text-muted-foreground">Score contesté</span>
                    <span className="text-[10px] text-muted-foreground">Raison & preuves</span>
                    <span className="text-[10px] text-muted-foreground">Confirmation</span>
                  </div>
                </div>

                <div className="p-6 space-y-5">
                  {step === 1 && (
                    <>
                      <div>
                        <h3 className="text-sm font-700 text-foreground mb-3">Quel score souhaitez-vous contester ?</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {scores.map(s => (
                            <button key={s} onClick={() => setSelectedScore(s)} className={`text-left px-4 py-3 rounded-xl border text-sm font-500 transition-all ${selectedScore === s ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-muted/30 text-foreground hover:border-primary/50'}`}>{s}</button>
                          ))}
                        </div>
                      </div>
                      {selectedScore && (
                        <div className="bg-muted/40 rounded-xl p-4">
                          <p className="text-xs font-600 text-foreground mb-2">Facteurs contributifs — {selectedScore}</p>
                          <p className="text-xs text-muted-foreground">Les facteurs ayant contribué à ce score vous seront détaillés après soumission de votre dossier, conformément à la politique de transparence ISX.</p>
                        </div>
                      )}
                      <button disabled={!selectedScore} onClick={() => setStep(2)} className="w-full py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl text-sm font-700 transition-all disabled:opacity-50">Continuer</button>
                    </>
                  )}

                  {step === 2 && (
                    <>
                      <div>
                        <h3 className="text-sm font-700 text-foreground mb-1">Score contesté : <span className="text-primary">{selectedScore}</span></h3>
                        <p className="text-xs text-muted-foreground mb-4">Sélectionnez la raison et fournissez des preuves</p>
                        <div className="space-y-2 mb-4">
                          {reasons.map(r => (
                            <button key={r} onClick={() => setSelectedReason(r)} className={`w-full text-left px-4 py-3 rounded-xl border text-sm font-500 transition-all ${selectedReason === r ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-muted/30 text-foreground hover:border-primary/50'}`}>{r}</button>
                          ))}
                        </div>
                        <label className="block text-xs font-600 text-muted-foreground uppercase tracking-wide mb-2">Preuves / Explication détaillée</label>
                        <textarea value={evidence} onChange={e => setEvidence(e.target.value)} rows={4} placeholder="Décrivez précisément les éléments qui justifient cette contestation. Vous pouvez mentionner des dates, des captures d'écran, des données spécifiques..." className="w-full bg-muted border border-border rounded-xl px-4 py-3 text-sm text-foreground outline-none focus:border-primary resize-none" />
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => setStep(1)} className="flex-1 py-3 bg-muted border border-border rounded-xl text-sm font-600 text-foreground hover:bg-muted/80 transition-colors">Retour</button>
                        <button disabled={!selectedReason} onClick={() => setStep(3)} className="flex-1 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl text-sm font-700 transition-all disabled:opacity-50">Continuer</button>
                      </div>
                    </>
                  )}

                  {step === 3 && (
                    <>
                      <h3 className="text-sm font-700 text-foreground mb-4">Confirmation de la contestation</h3>
                      <div className="space-y-3 mb-4">
                        {[{ label: 'Score contesté', value: selectedScore }, { label: 'Raison', value: selectedReason }].map(item => (
                          <div key={item.label} className="flex justify-between items-start py-2 border-b border-border/50">
                            <span className="text-xs text-muted-foreground">{item.label}</span>
                            <span className="text-xs font-600 text-foreground text-right max-w-[60%]">{item.value}</span>
                          </div>
                        ))}
                        {evidence && (
                          <div className="bg-muted/40 rounded-lg p-3">
                            <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-600 mb-1">Preuves fournies</p>
                            <p className="text-xs text-foreground">{evidence}</p>
                          </div>
                        )}
                      </div>
                      <div className="bg-muted/40 rounded-xl p-4 mb-4 space-y-2">
                        <p className="text-xs font-600 text-foreground">Ce que vous acceptez :</p>
                        <ul className="space-y-1">
                          {['La contestation est gratuite et sans limite', 'Un numéro de dossier vous sera attribué immédiatement', 'La décision peut faire l\'objet d\'une révision humaine', 'Les conflits d\'intérêts du reviewer sont vérifiés', 'Vous serez notifié à chaque étape'].map(item => (
                            <li key={item} className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Icon name="CheckIcon" size={12} className="text-positive flex-shrink-0" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => setStep(2)} className="flex-1 py-3 bg-muted border border-border rounded-xl text-sm font-600 text-foreground hover:bg-muted/80 transition-colors">Retour</button>
                        <button onClick={handleSubmit} className="flex-1 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl text-sm font-700 transition-all">Soumettre la contestation</button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* History */}
        {activeTab === 'history' && (
          <div className="space-y-4">
            <h2 className="text-base font-700 text-foreground">Mes dossiers de contestation</h2>
            {existingCases.map(c => (
              <div key={c.caseId} className="bg-card border border-border rounded-xl p-5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-sm font-700 text-primary">{c.caseId}</span>
                      <span className={`text-[10px] font-700 uppercase tracking-wide px-2 py-0.5 rounded-full border ${statusMap[c.status].cls}`}>{statusMap[c.status].label}</span>
                    </div>
                    <p className="text-sm text-foreground font-600">{c.score}</p>
                    <p className="text-xs text-muted-foreground">{c.reason} · {c.date}</p>
                  </div>
                  {c.status === 'under_review' && (
                    <div className="flex items-center gap-1.5 text-xs text-warning">
                      <Icon name="ClockIcon" size={14} />
                      <span>~{c.estimatedDays} jours restants</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* GDPR */}
        {activeTab === 'gdpr' && (
          <div className="max-w-2xl space-y-6">
            <div className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-base font-700 text-foreground mb-4">Vos droits RGPD</h2>
              <div className="space-y-3">
                {[
                  { icon: 'ArrowDownTrayIcon', title: 'Télécharger mes données', desc: 'Exportez l\'intégralité de vos données personnelles stockées par ISX au format JSON/CSV.', action: 'Demander l\'export', color: 'text-primary' },
                  { icon: 'TrashIcon', title: 'Supprimer mon compte', desc: 'Supprimez définitivement votre compte et toutes vos données. Cette action est irréversible. Les données de sous-traitants et sauvegardes seront également traitées selon notre politique.', action: 'Demander la suppression', color: 'text-negative' },
                  { icon: 'DocumentTextIcon', title: 'Accéder à mes données', desc: 'Consultez toutes les données personnelles que ISX détient sur vous.', action: 'Voir mes données', color: 'text-info' },
                  { icon: 'ArrowPathIcon', title: 'Portabilité des données', desc: 'Recevez vos données dans un format structuré et lisible par machine.', action: 'Demander la portabilité', color: 'text-teal-400' },
                ].map(item => (
                  <div key={item.title} className="flex items-start gap-4 p-4 bg-muted/30 rounded-xl border border-border/50">
                    <div className={`w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 ${item.color}`}>
                      <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-700 text-foreground">{item.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
                    </div>
                    <button className={`flex-shrink-0 px-3 py-1.5 bg-muted border border-border rounded-lg text-xs font-600 hover:bg-muted/80 transition-colors ${item.color}`}>{item.action}</button>
                  </div>
                ))}
              </div>
            </div>

            {/* Consent Management */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-base font-700 text-foreground mb-4">Gestion du consentement</h2>
              <div className="space-y-3">
                {[
                  { label: 'Traitement des données de scoring', required: true, checked: true },
                  { label: 'Partage agrégé anonymisé (Observatory)', required: false, checked: true },
                  { label: 'Notifications par email', required: false, checked: true },
                  { label: 'Données partagées avec les marques (scores publics)', required: false, checked: false },
                ].map(item => (
                  <div key={item.label} className="flex items-center justify-between py-2 border-b border-border/50">
                    <div>
                      <p className="text-sm text-foreground">{item.label}</p>
                      {item.required && <p className="text-[10px] text-muted-foreground">Requis pour le fonctionnement du service</p>}
                    </div>
                    <div className={`w-10 h-5 rounded-full transition-all cursor-pointer flex items-center px-0.5 ${item.checked ? 'bg-primary' : 'bg-muted border border-border'}`}>
                      <div className={`w-4 h-4 rounded-full bg-white shadow transition-all ${item.checked ? 'translate-x-5' : 'translate-x-0'}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Legal */}
            <div className="bg-muted/30 border border-border rounded-xl p-4">
              <p className="text-[11px] text-muted-foreground leading-relaxed">Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez des droits d'accès, de rectification, d'effacement, de portabilité, d'opposition et de limitation du traitement. ISX traite vos données sur la base de votre consentement et de l'exécution du contrat de service. Toute demande sera traitée dans un délai de 30 jours. Pour toute question : privacy@isx-reversal.com</p>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
